/**
 * Date helpers for the events calendar.
 *
 * All event dates are stored as plain 'YYYY-MM-DD' strings and treated as
 * *local calendar dates*, we never let the browser's timezone shift a date by
 * a day. `parseDate` builds a Date at local midnight from the y/m/d parts, and
 * every comparison below works on those normalized dates.
 */

/**
 * Event location privacy: the venue is only revealed once an event is approved
 * (a future accounts/confirmation flow will set `locationApproved` on the event).
 * Until then every event's location renders as "Private".
 */
export const isEventLocationApproved = (event) => event?.locationApproved === true;
/** Short label for compact spots (cards, structured data). */
export const privateLocationLabel = (lang) => (lang === 'en' ? 'Private' : 'פרטי');
/** Fuller message for detail views. */
export const privateLocationMessage = (lang) =>
    lang === 'en'
        ? 'The location is private and will be shared after confirmation.'
        : 'המיקום פרטי ויישלח לאחר האישור.';
/** Airbnb-style "rare find" badge copy. */
export const privateLocationTitle = (lang) => (lang === 'en' ? 'Private location' : 'מיקום פרטי');
export const privateLocationSubtitle = (lang) =>
    lang === 'en' ? 'Exclusive, shared after confirmation' : 'אקסקלוסיבי, יישלח לאחר האישור';

/** 'YYYY-MM-DD' → Date at local midnight. Returns null for falsy/invalid input. */
export function parseDate(str) {
    if (!str) return null;
    if (str instanceof Date) return new Date(str.getFullYear(), str.getMonth(), str.getDate());
    const [y, m, d] = String(str).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}

/** Local midnight of a Date (strips the time component). */
export function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date, n) {
    const d = startOfDay(date);
    d.setDate(d.getDate() + n);
    return d;
}

export function isSameDay(a, b) {
    return a && b &&
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
}

/** Inclusive day-count difference (b - a) in whole days. */
export function daysBetween(a, b) {
    return Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
}

/**
 * Build a month matrix: an array of weeks, each a 7-element array of Dates,
 * starting on Sunday and padded with the surrounding month's days so every
 * week is full. `month` is 0-based.
 */
export function buildMonthMatrix(year, month) {
    const first = new Date(year, month, 1);
    const gridStart = addDays(first, -first.getDay()); // back up to the Sunday
    const weeks = [];
    let cursor = gridStart;

    // Always emit 6 weeks so the grid has a constant row count (and therefore a
    // constant height) regardless of the month, this keeps the calendar from
    // resizing and pushing surrounding content when navigating months.
    for (let w = 0; w < 6; w++) {
        const week = [];
        for (let d = 0; d < 7; d++) {
            week.push(cursor);
            cursor = addDays(cursor, 1);
        }
        weeks.push(week);
    }
    return weeks;
}

/** Does the event's [startDate, endDate] range intersect [rangeStart, rangeEnd]? */
export function eventInRange(event, rangeStart, rangeEnd) {
    const s = parseDate(event.startDate);
    const e = parseDate(event.endDate) || s;
    if (!s) return false;
    return s <= rangeEnd && e >= rangeStart;
}

/** All events touching a given day, sorted (multi-day first, then by time). */
export function eventsOnDay(events, day) {
    return events
        .filter((ev) => eventInRange(ev, day, day))
        .sort((a, b) => {
            const am = a.isMultiDay ? 0 : 1;
            const bm = b.isMultiDay ? 0 : 1;
            if (am !== bm) return am - bm;
            return String(a.startTime || '').localeCompare(String(b.startTime || ''));
        });
}

/**
 * Compute the event "bars" for one week of the calendar.
 *
 * For each event overlapping the week it returns:
 *   { event, startCol, span, isStart, isEnd, lane }
 * where startCol is 0-6 (Sunday-based, logical), span is the number of columns
 * it covers within this week, and lane is the vertical row assigned so bars
 * never overlap. `dir` on the rendering container flips this visually for RTL.
 */
export function getWeekSegments(week, events) {
    const weekStart = week[0];
    const weekEnd = week[6];

    const segments = events
        .map((event) => {
            const s = parseDate(event.startDate);
            const e = parseDate(event.endDate) || s;
            if (!s || !(s <= weekEnd && e >= weekStart)) return null;

            const visibleStart = s < weekStart ? weekStart : s;
            const visibleEnd = e > weekEnd ? weekEnd : e;
            const startCol = daysBetween(weekStart, visibleStart);
            const span = daysBetween(visibleStart, visibleEnd) + 1;

            return {
                event,
                startCol,
                span,
                isStart: isSameDay(visibleStart, s),
                isEnd: isSameDay(visibleEnd, e),
                _s: s,
                _e: e,
            };
        })
        .filter(Boolean)
        // Longer / earlier events take the top lanes.
        .sort((a, b) => (b.span - a.span) || (a._s - b._s));

    // Greedy lane assignment: first lane where this segment's columns are free.
    const lanes = [];
    segments.forEach((seg) => {
        let lane = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const occupied = lanes[lane] || [];
            const clash = occupied.some(
                (o) => seg.startCol < o.startCol + o.span && o.startCol < seg.startCol + seg.span
            );
            if (!clash) {
                lanes[lane] = [...occupied, seg];
                seg.lane = lane;
                break;
            }
            lane++;
        }
    });

    return { segments, laneCount: lanes.length };
}

// ─── Human-readable formatting ──────────────────────────────────────────────

const locale = (language) => (language === 'en' ? 'en-US' : 'he-IL');

export function formatDateRange(startDate, endDate, language = 'he') {
    const s = parseDate(startDate);
    const e = parseDate(endDate) || s;
    if (!s) return '';

    const loc = locale(language);
    const full = { year: 'numeric', month: 'long', day: 'numeric' };

    if (isSameDay(s, e)) return s.toLocaleDateString(loc, full);

    // Same month → "August 28-30, 2026" / "28-30 באוגוסט 2026"
    if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
        if (language === 'en') {
            const month = s.toLocaleDateString(loc, { month: 'long' });
            return `${month} ${s.getDate()}, ${e.getDate()}, ${s.getFullYear()}`;
        }
        const month = s.toLocaleDateString(loc, { month: 'long' });
        return `${s.getDate()}, ${e.getDate()} ב${month} ${s.getFullYear()}`;
    }

    return `${s.toLocaleDateString(loc, full)}, ${e.toLocaleDateString(loc, full)}`;
}

export function formatTimeRange(startTime, endTime, language = 'he') {
    if (!startTime) return '';
    if (!endTime) return startTime;
    return language === 'en' ? `${startTime}, ${endTime}` : `${startTime}, ${endTime}`;
}

export function formatWeekday(startDate, language = 'he') {
    const s = parseDate(startDate);
    if (!s) return '';
    return s.toLocaleDateString(locale(language), { weekday: 'long' });
}

export function formatMonthYear(date, language = 'he') {
    return date.toLocaleDateString(locale(language), { month: 'long', year: 'numeric' });
}

export function isPastEvent(event) {
    const e = parseDate(event.endDate) || parseDate(event.startDate);
    if (!e) return false;
    return e < startOfDay(new Date());
}
